/**
 * useAlgorithmConfig
 *
 * Fetches and caches admin-tweakable algorithm parameters from Supabase.
 * Follows "everything is a parameter" philosophy - no hardcoded algorithm values.
 *
 * Usage:
 *   const { getConfig, easyConfig, fastConfig, isLoaded } = useAlgorithmConfig(supabase)
 *   await loadConfigs()
 *   const pauseMs = fastConfig.value.pause_base_ms
 */

import { ref, computed, type Ref } from 'vue'
import { type RatePolicyBounds, DEFAULT_RATE_POLICY_BOUNDS, computePauseDuration } from '@ssi/core'
import { countSyllables, hasSyllableCounter, syllableLangOf } from '@ssi/core/text'
import { DEFAULT_REPEATED_CYCLE_TYPES, MAX_PHRASE_REPEAT_COUNT } from '../providers/repeatPhraseCycles'
import {
  type ListeningRampStep,
  type ListeningSlot,
  type ListeningSpeedSource,
  type ListeningBeltCeiling,
  DEFAULT_EASY_BELT_CEILINGS,
  DEFAULT_EASY_LISTENING_RAMP,
  DEFAULT_FAST_BELT_CEILINGS,
  DEFAULT_FAST_LISTENING_RAMP,
  DEFAULT_LISTENING_PATTERN,
  DEFAULT_LISTENING_SPEED_SOURCE,
  LISTENING_SPEED_CEILING,
  normalizeBeltCeilings,
  normalizeListeningRamp,
  resolveListeningPattern,
} from '../playback/listeningExposureRamp'
import {
  type EncouragementTaperConfig,
  DEFAULT_ENCOURAGEMENT_TAPER,
} from '../services/MetaCommentaryService'

// Type definitions for algorithm configs
export interface ModeConfig {
  playback_speed: number      // 1.0 = normal, 1.25 = 25% faster
  /** Thinking multiplier on the native (1.0×) target-sentence duration.
   *  gap = pause_k × answer + pause_reaction_ms. See computePauseDuration.ts. */
  pause_k?: number
  /** Fixed reaction beat (ms) added on top of the multiplied answer duration. */
  pause_reaction_ms?: number
  min_pause_ms: number        // Safety floor for pause duration (not tuning)
  max_pause_ms: number        // Safety ceiling for pause duration (not tuning)
  /** RETIRED 2026-08-29 (one-formula gap model). Live `algorithm_config` rows
   *  still carry these; computePauseDuration IGNORES every one of them. Kept on
   *  the type only so a stored row still parses. Do not read them. */
  pause_base_ms?: number
  pause_multiplier?: number
  pause_reference?: 'avg' | 'target1' | 'sum'
  pause_knee_ms?: number
  pause_tail_multiplier?: number
  pause_boot_ms?: number
  pause_assembly_threshold_ms?: number
  pause_assembly_lin?: number
  pause_assembly_quad?: number
  pause_belt_boot?: number
  pause_belt_assembly?: number
  /**
   * Extra silence (ms) held AFTER voice2, before the next cycle's prompt —
   * "to stop the next cycle just coming in and taking over" (Tom, 2026-08-07).
   * Voice2 is the phase carrying the target text on screen, so the same gap
   * also leaves that text up for longer. Easy holds 1s; Fast holds none.
   * Absent / 0 ⇒ the historic behaviour (next cycle starts immediately).
   */
  post_voice2_gap_ms?: number
  spaced_rep_fraction: number // 1.0 = full, 0.33 = skip 2/3
  debut_phrases_fraction: number // 1.0 = all, 0.5 = half
  skip_voice2: boolean        // Skip second target voice?
  /**
   * OPTIONAL per-mode override of the GLOBAL `script_shape` row. Keys mirror
   * ScriptShapeConfig exactly; anything omitted falls through to the global
   * value. Layering happens in exactly ONE place — `resolveScriptShape()`
   * below — so there is no second merge rule to keep in sync.
   *
   * This is where Easy and Fast are allowed to differ in SHAPE, and it is the
   * ONLY way they may: a mode GENERATES a different round, it never plays a
   * subset of one. There is no per-cycle cull on the mode path — SimplePlayer's
   * `shouldSkipCycle` hook belongs to adaptation v2 alone.
   */
  scriptShape?: Partial<ScriptShapeConfig>
  /**
   * Cap on phrase LENGTH, expressed as a fraction of the longest phrase in
   * the WHOLE COURSE (not per-LEGO — see courseMaxPhraseLength). Range (0, 1].
   *   1.0 — uncapped; today's exact behaviour. Fast ships 1.0, so Fast is
   *         provably unchanged.
   *   0.5 — Easy: half the longest possible phrase (Aran, 2026-08-06).
   * Applied by `capPhrasesByLength()` below — the ONE place the rule lives.
   * Sorting is always shortest-first; this is a CAP, not a sort direction.
   * A missing or invalid value degrades to 1.0 (uncapped), never to a cap.
   */
  maxPhraseLengthFraction?: number
  /**
   * How many times each practice cycle plays, back to back — "in EASY mode,
   * double up every phrase, every BLD, every USE, every REVIEW, every
   * CONSOLIDATE" (Tom, 2026-08-07). Easy ships 2; Fast ships 1.
   *
   * HARD CEILING OF 2, from Tom's rule rather than from taste: "we do NOT ever
   * want to repeat exactly the same phrase more than 2x - a phrase repeated 3x
   * would drive people nuts, but doubled up is perfect". A row asking for 3 is
   * clamped to 2 and warns. Absent / ≤1 ⇒ no repetition at all.
   */
  phraseRepeatCount?: number
  /**
   * WHICH cycle types `phraseRepeatCount` applies to. Default is the four Tom
   * named — BLD, REVIEW, USE and CONSOLIDATE, which are `build`, `spaced_rep`
   * and `use` in script terms. The INTRO and bare-LEGO debut are absent by his
   * ruling ("of course not - the intro LEGO and not the LEGO alone"); adding
   * them here is a config decision, not a code change.
   *
   * SEED-PHASE production reviews are never repeated whatever this says — that
   * sandwich is already several cycles of one sentence, so repeating it would
   * breach the never-more-than-twice rule. Structural, not a setting.
   */
  repeatedCycleTypes?: string[]
  /**
   * Should BUILD phrases be put through the phrase-length filters at all?
   *
   * Tom, 2026-08-07: "no filtering on BLD phrases". Build phrases are short by
   * construction — a new LEGO plus one or two already-known ones — and they
   * ARE the debut round, so filtering them guts the round it is meant to
   * gentle. Easy therefore takes its BUILD pool whole; USE pools still get the
   * character cap. Absent ⇒ true (filter), which is the historic behaviour.
   */
  filterBuildPhrases?: boolean
  /**
   * Pull-time ceiling on a review/consolidate phrase's KNOWN-language
   * syllables (Tom, 2026-08-07). Note all three things this is NOT: it counts
   * the KNOWN side (the prompt the learner hears in their own language), not
   * the target; it is a filter on WHICH use phrase is pulled from a LEGO's
   * basket for a REVIEW or CONSOLIDATE slot, not a ceiling over the whole
   * script; and it lifts entirely past `reviewSyllableFilterMaxRound`.
   *
   * If a LEGO's basket holds nothing at or under the cap, the SHORTEST phrase
   * in the basket is pulled instead — the round is never left empty and the
   * LEGO is never skipped.
   *
   * Absent / ≤0 ⇒ no filter, which is Fast's value. Easy ships 15.
   */
  reviewMaxKnownSyllables?: number
  /**
   * Last course round on which `reviewMaxKnownSyllables` applies. From the
   * next round the filter simply comes off — nothing is backlogged and
   * nothing cascades. Tom, 2026-08-07: "the whole idea of you've got a
   * cascade, you've got a wall, once you get to 100 and 101, all these space
   * repetitions is complete nonsense, makes no difference at all", because
   * "it's the LEGO that you are practicing" and the phrase carrying it need
   * never have been met before. Easy ships 100.
   */
  reviewSyllableFilterMaxRound?: number
  /**
   * SLIDING WORD-COUNT CAP on USE phrases, by course round (Tom, 2026-08-09).
   * A ladder of `{maxRound, maxWords}` rungs: the first rung whose `maxRound`
   * the current round is at or under decides the ceiling; past the last rung
   * the cap comes off entirely. Easy ships 8 words to round 20, 10 words to
   * round 100, uncapped from 101.
   *
   * WORDS, not milliseconds. Duration was measured first and rejected: baked-in
   * leading/trailing silence differs by language and by render batch, so an ms
   * ceiling ranked a short Arabic phrase as longer than a rambling French one.
   *
   * Counted on whichever side of the pair is ENGLISH — known_text when English
   * is the known language, target_text when English is the target. The scale
   * was validated on English-involved courses only (fra_for_eng, eng_for_zho,
   * eng_for_ara, eng_for_hin, eng_for_spa), so it applies ONLY where English is
   * on one side. A course with English on neither side (kor_for_tam, and 46
   * others) gets no cap at all — deferred, deliberately, not blocked.
   *
   * STARVATION GUARD: if a round has no USE phrase left under the ceiling, the
   * cap comes off for that round rather than leaving it empty.
   *
   * Absent / malformed / empty ⇒ no cap, which is Fast's value.
   */
  useWordCapTiers?: UseWordCapTier[]
  /**
   * HOW OFTEN THE LAYER-2 PODS COME ROUND, IN THIS MODE (Tom, 2026-09-18:
   * "The listening exercises every ROUND are fine — the CUPS. The PODS (layer
   * 2 listening exercises) need to come more often on EASY MODE. Every 2x
   * ROUNDS in EASY MODE and every 4x ROUNDS in FAST mode.").
   *
   * A work DEBT, not a position rule — `usePodLapScheduler` counts every
   * completed round and fires a lap at the first clean boundary where the debt
   * reaches this number, so the cadence survives replays, belt skips and a
   * crawling position. Easy ships 2, Fast ships 4.
   *
   * Overrides the global `algorithm_config.pods.roundInterval`, which stays as
   * the fallback for a mode row that carries no value of its own. Absent /
   * <1 ⇒ that fallback.
   */
  podRoundInterval?: number
  /**
   * LISTENING speed ramp for this mode, over a phrase's EXPOSURES (Tom,
   * 2026-08-07): "it might be 0.8. Or maybe even Notepoint. Seven, the very
   * first time, and it might be 0.8, then it might stay on 0.8 for a few
   * times, then it might go up to one, never more than one, depending on what
   * mode we're in. Fast can probably start at the regular speed."
   *
   * A step table: `[{speed, plays}, …]`, walked by exposure count, last step
   * terminal. ALL four slots of a phrase — including the KNOWN-language one —
   * play at the step's speed, so a phrase is internally uniform.
   *
   * Easy ships 0.7 ×1 → 0.8 ×4 → 1.0 for ever; Fast ships a single 1.0 step.
   * 1.0 is a HARD ceiling enforced in code (LISTENING_SPEED_CEILING), not by
   * these values — a DB row asking for 1.5 still plays at 1.0.
   *
   * Absent / malformed ⇒ the shipped ramp for that mode (never "no ramp":
   * that would hand a beginner full speed, which is the bug this fixes).
   */
  listeningSpeedRamp?: ListeningRampStep[]
  /**
   * BELT CEILING table for listening in this mode — Tom, 2026-08-07 23:56Z,
   * correcting the exposure-only reading that shipped first: "for Easy the BELT
   * TABLE is authoritative... 0.8x for white/yellow belt, 0.9x for orange/green,
   * 1.0x for blue and beyond, NEVER above 1.0. The per-exposure ramp applies
   * UNDERNEATH the belt ceiling."
   *
   * So the two ramps COMPOSE rather than compete: the belt rung is the maximum
   * for that learner, and `listeningSpeedRamp` is what approaches it from below
   * on early hearings. A white-belt Easy learner never exceeds 0.8x however
   * many times they have heard the phrase.
   *
   * Keyed on the LEARNER's position, not the replayed phrase's — Tom's wording
   * is learner-centric ("a white-belt LEARNER's ceiling").
   *
   * Easy ships white/yellow 0.8, orange/green 0.9, blue+ 1.0. Fast ships a
   * single 1.0 rung, i.e. no ceiling — "this correction is Easy-only".
   * Absent / malformed ⇒ the shipped table for that mode (never "no ceiling").
   */
  listeningBeltCeilings?: ListeningBeltCeiling[]
}

/**
 * One rung of the Easy USE word-count ladder — see ModeConfig.useWordCapTiers.
 * `maxRound` is inclusive; `maxWords` is the ENGLISH-side ceiling on that span.
 */
export interface UseWordCapTier {
  maxRound: number
  maxWords: number
}

/**
 * Easy's shipped ladder (Tom, 2026-08-09): 8 words for rounds 1-20, 10 words
 * for rounds 21-100, uncapped from 101. A DB row on
 * `algorithm_config.easy_mode.useWordCapTiers` overrides it, so retuning by ear
 * is a Supabase edit rather than a deploy.
 */
export const DEFAULT_EASY_USE_WORD_CAP_TIERS: readonly UseWordCapTier[] = [
  { maxRound: 20, maxWords: 8 },
  { maxRound: 100, maxWords: 10 },
]

/** The two learning modes (Aran's ruling 2026-08-06). Easy is the default. */
export type LearningMode = 'easy' | 'fast'

/**
 * THE default: the mode every new learner starts on (Tom, 2026-09-28: "new
 * learners start on EASY"). The single source of truth for it — the new-learner
 * rule in newLearnerMode.ts reads this constant rather than naming a mode.
 *
 * It is not "the mode of anyone who never chose": learners already playing
 * before the Easy default landed kept Fast, and still do. That carve-out lives
 * in newLearnerMode.ts as LEGACY_UNCHOSEN_MODE, so nobody's chosen or settled
 * pace moves underneath them.
 */
export const DEFAULT_LEARNING_MODE: LearningMode = 'easy'

/** `algorithm_config` row key for a mode. */
export const MODE_CONFIG_KEY: Record<LearningMode, string> = {
  easy: 'easy_mode',
  fast: 'fast_mode',
}

/**
 * Listening slot roles — full set including runtime playback rates.
 *   ps08x = 0.8×, ps = 1×, ps15x = 1.5×, ps2x = 2×, trans = known audio at 1×
 */
export type ListeningSlotRole = 'ps08x' | 'ps' | 'ps15x' | 'ps2x' | 'trans'
// Pods use the same slot roles (the 'explainer' role was retired 2026-10-04).
export type PodSlotRole = ListeningSlotRole

/** Layer 2 (Listening Pod) scheduler — gap matrix + stage progression. */
export interface PodsConfig {
  stagePlaylist: Record<string, PodSlotRole[]>  // keyed by stage number as string
  stageDuration: number       // pod-rounds per transitional stage; highest key = eternal
  /** Per-stage duration overrides (e.g. {'1': 2, '2': 3} — Phase 0 explainer
   *  plays twice, Phase 1 three times). Unlisted stages use stageDuration. */
  stageDurations?: Record<string, number>
  /**
   * TEN VISITS PER SENTENCE, THEN IT RETIRES (Tom, 2026-09-19): "each stage
   * should repeat once — for simplicity — apart from Stage 9, which should
   * retire after maybe 2 repeats, else we're just building up the listening
   * until it's too long. This will mean each 'sentence' goes through a total
   * of 10 'visits'."
   *
   * The ladder's whole length in LAPS THE SENTENCE ACTUALLY APPEARS IN — a
   * skipped session burns no visit. It WINS over whatever `stageDurations` /
   * `stageDuration` sum to, so the terminal rung absorbs the remainder and a
   * sentence can never outstay it however many rungs the playlist carries.
   * Absent ⇒ the shipped 10; 0 or negative ⇒ back to the derived length.
   */
  retireAfterVisits?: number
  gapSuperTightMs: number     // known→target, target→target
  gapTightMs: number          // target→known
  gapGluedMs: number          // chunk → glued chunk (early stages)
  gapBetweenMs: number        // chunk → non-glued, intro→first, last→outro
  /** Fire a pod-lap every N main rounds from podActivationRound onward.
   *  Default 1 (every round, legacy behaviour). Stretches every pod stage
   *  proportionally because the pod-round ratchet only ticks on actual
   *  fires — not session rounds. */
  roundInterval: number
  /** Main-round at which Layer 2 pods START FIRING for new learners.
   *  Returning learners get their own pin on `course_enrollments.pod_activation_round`
   *  which always wins over this default. Moved here from ListeningModeConfig
   *  so the admin "pod activation" knob lives next to the other pod controls. */
  podActivationRound: number
}

/**
 * Resume regression — long absences re-engage the learner with familiar
 * territory. Cursor regression is one-way (only on resume); ceiling
 * (highest_completed_*) is always preserved so they keep access.
 */
export interface ResumeConfig {
  /** After this gap in MINUTES, ignore current_cycle_index on resume and
   *  restart the in-progress round (replays the LEGO intro). A brief pause
   *  resumes the exact cycle; a real break restarts the round. ~5 min cutoff. */
  cycleResetMinutes: number
  /** After this gap in days, walk the round cursor back to the start of
   *  the learner's current belt. Set to a very large number to disable. */
  beltRegressionDays: number
}

/** Per-round script shape — phrase counts + Fibonacci spaced-rep schedule. */
export interface ScriptShapeConfig {
  spacedRepOffsets: number[]   // fib offsets at which spaced rep fires
  maxBuildPhrases: number      // BUILD slots per round
  useConsolidationCount: number  // USE phrases per LEGO
  maxSpacedRepPhrases: number    // total spaced-rep cap per round
  n1PhraseCount: number          // phrases at the N-1 review (vs 1 elsewhere)
}

/**
 * Layer 1 + Layer 2 listening config — mirrored to DB row
 * `algorithm_config` key='listening'. Admins tweak in Supabase Studio
 * and the change takes effect on next session (5-min cache TTL).
 */
export interface ListeningModeConfig {
  enabled: boolean
  offset: number              // rounds after last LEGO before seed graduates
  /** @deprecated Moved to PodsConfig.podActivationRound (2026-05-17).
   *  Optional/read-only here for legacy rows whose `pods` config hasn't
   *  been re-saved yet. The dashboard backfills on next load.
   *  KEPT: still the #2 fallback link for un-migrated `listening` config
   *  rows (LearningPlayer.vue) — removing changes pod-activation timing
   *  for un-migrated learners. Keep until the dashboard backfills `pods`. */
  podActivationRound?: number
  /**
   * THE listening play pattern (Tom, 2026-08-07): "Every single phrase. It's
   * played Target, Known, Target, Target. And I think that's all that
   * happens." One pattern, mode-agnostic, layer-agnostic — Layer 1 maps it
   * onto a seed's two recorded voices, Layer 2 onto a pod sentence's target
   * and translation clips.
   *
   * This SUPERSEDES the nine-stage `PodsConfig.stagePlaylist`, which no longer
   * drives listening at all (see `listeningUseStagePlaylist` below for the one
   * escape hatch). Absent / malformed ⇒ DEFAULT_LISTENING_PATTERN. A pattern
   * ending on 'known' is rejected — never strand the learner on their own
   * language.
   */
  playPattern?: ListeningSlot[]
  /**
   * Ceiling on any listening playback rate. Intersected with the code constant
   * LISTENING_SPEED_CEILING (1.0), so this key can only ever lower the ceiling
   * — "never more than one" is not negotiable from the DB.
   */
  maxSpeed?: number
  /**
   * Which axis decides a listening clip's speed:
   *
   *   'exposure' (default, Tom 2026-08-07) — the per-mode step table over how
   *      many times the learner has met THIS phrase (ModeConfig.listeningSpeedRamp).
   *   'belt' — the historical name for the `computeListeningSpeed` path.
   *
   * The name is now vestigial: Tom ruled on 2026-08-16 that LISTENING IS NEVER
   * SLOWED, and `computeListeningSpeed` no longer carries a belt term, so this
   * key selects which machinery computes the rate, not whether the belt gates
   * it. Both shipped defaults land on full pace. The belt curve lives on for
   * SPEAKING only, in `computeCycleSpeed`.
   */
  speedSource?: ListeningSpeedSource
  /**
   * ESCAPE HATCH ONLY — replay the retired nine-stage `PodsConfig.stagePlaylist`
   * instead of the single pattern. Default false; there is no learner-facing
   * way to reach it. It exists so the old behaviour can be restored from the
   * DB row if the simplification turns out wrong on Tom's ear, not as a
   * supported alternative mode.
   */
  listeningUseStagePlaylist?: boolean
}

/**
 * Meta-commentary knobs — DB row `algorithm_config` key='meta_commentary'.
 * Encouragement taper (owner ruling 2026-08-06, superseding 2026-07-24):
 * random encouragements dial down with learner experience and switch fully off
 * past a threshold. The experience unit is the learner's CUMULATIVE LEARNING
 * MINUTES ACROSS ALL COURSES — not their position in the current course, which
 * made a veteran starting a new course look like a beginner. Defaults: taper
 * starts at 600 min (10h), off at 1800 min (30h). Instructions (the once-ever
 * science bits) are never tapered.
 */
export interface MetaCommentaryConfig {
  encouragementTaper: EncouragementTaperConfig
}

export interface AlgorithmConfigs {
  fast_mode: ModeConfig
  easy_mode: ModeConfig
  listening: ListeningModeConfig
  pods: PodsConfig
  script_shape: ScriptShapeConfig
  resume: ResumeConfig
  adaptation_v2: AdaptationV2Config
  meta_commentary: MetaCommentaryConfig
  [key: string]: ModeConfig | ListeningModeConfig | PodsConfig | ScriptShapeConfig | ResumeConfig | AdaptationV2Config | MetaCommentaryConfig
}

// Default fallbacks (used if DB fetch fails)
//
// Pause formula: clamp(min_pause_ms, max_pause_ms, pause_k × answer + pause_reaction_ms),
// where `answer` is the average NATIVE (1.0×) duration of the target sentence's two
// voices. One formula, two tunables per mode — see computePauseDuration.ts.
export const DEFAULT_FAST: ModeConfig = {
  playback_speed: 1.0,
  // One-formula gap (Tom, 2026-08-29): gap = k × native answer duration +
  // reaction beat. Starting constants fitted to what the live fast_mode row
  // produced for 1–3s answers under the old boot+assembly curve.
  pause_k: 2.8,
  pause_reaction_ms: 800,
  min_pause_ms: 1000,
  max_pause_ms: 15000,
  // Fast is unchanged: the next cycle follows voice2 immediately, as it always has.
  post_voice2_gap_ms: 0,
  spaced_rep_fraction: 1.0,
  debut_phrases_fraction: 1.0,
  skip_voice2: false,
  // Identity override: Fast generates EXACTLY the global script_shape, so its
  // behaviour is provably unchanged from the pre-2026-08-06 'normal_mode'.
  scriptShape: {},
  // Uncapped phrase length — Fast meets exactly the phrases it always did.
  maxPhraseLengthFraction: 1.0,
  // Every Easy lever explicitly OFF, so Fast's script is provably unchanged
  // by the 2026-08-07 redesign: each cycle plays once, BUILD filtered as it
  // always was, no known-side pull filter. The type list is carried even
  // though the count of 1 makes it inert, so both modes present the same
  // knobs on the admin page.
  phraseRepeatCount: 1,
  repeatedCycleTypes: ['build', 'spaced_rep', 'use'],
  filterBuildPhrases: true,
  reviewMaxKnownSyllables: 0,
  // No USE word cap — Fast meets exactly the phrases it always did, on every
  // course, English-involved or not.
  useWordCapTiers: [],
  /**
   * LISTENING — "Fast can probably start at the regular speed" (Tom,
   * 2026-08-07). One terminal step at 1.0×, which is also the ceiling, so Fast
   * listening never ramps: it is flat native pace, modulated only by the
   * course's own globalSpeed. It still plays the SAME four-slot pattern as
   * Easy, at one uniform speed per phrase — the pattern is the mode-agnostic
   * part of the redesign; only the ramp differs by mode.
   */
  listeningSpeedRamp: DEFAULT_FAST_LISTENING_RAMP,
  // No belt ceiling — a single 1.0 rung. Tom, 2026-08-07: "Fast may still start
  // at 1.0 as you built it — this correction is Easy-only."
  listeningBeltCeilings: DEFAULT_FAST_BELT_CEILINGS,
  // Pods every 4 rounds in Fast (Tom, 2026-09-18).
  podRoundInterval: 4,
}

/**
 * EASY mode — the gentler of the two modes (Aran's ruling 2026-08-06: exactly
 * two modes, Turbo deleted).
 *
 * TUNING STATUS: these are FIRST-PASS defaults, chosen to be coherent, not
 * calibrated by ear. Every value is a DB row (`algorithm_config.easy_mode`),
 * so retuning Easy is a Supabase edit, not a deploy.
 *
 * These MIRROR the seeded DB rows (Popty's scripts/learning-modes/
 * create-mode-rows.cjs) and are pitched at roughly a learner's FIRST 10 HOURS,
 * adjusting upwards from there (Aran, 2026-08-06). Aran's three seeds: double
 * the time, double the reps, halve the longest possible phrase. On the time he
 * added that it "does not need to be dramatic", so the doubling is confined to
 * boot + floor and deliberately does NOT touch the assembly slope or the
 * ceiling — doubling those too would compound into a sluggish gap on long
 * sentences.
 *
 * SPEED: Easy does NOT have a speed of its own. It rides the same belt/target-
 * language ramp as Fast (Tom's ruling, 2026-08-07: "Easy should follow the
 * exact speed pattern on-ramps for the target language as Fast — but just with
 * bigger pauses, more repetitions and so on"). Easy used to force a flat 1.0×,
 * which made a White-belt beginner on Easy hear FASTER speech than the same
 * beginner on Fast. `playback_speed` below is therefore INERT for both modes —
 * kept only because the DB row carries the column.
 */
export const DEFAULT_EASY: ModeConfig = {
  playback_speed: 1.0,
  // One-formula gap (Tom, 2026-08-29). Easy keeps its longer gaps by carrying
  // a bigger k, not by a separate curve; fitted to the live easy_mode row's
  // output for 1–3s answers.
  pause_k: 3.6,
  pause_reaction_ms: 800,
  min_pause_ms: 2000,
  max_pause_ms: 15000,
  // A beat of silence after voice2 so the next cycle doesn't come straight in
  // over the top of the one just heard, and the target text stays on screen
  // that bit longer (Tom, 2026-08-07). Easy only — Fast holds 0.
  post_voice2_gap_ms: 1000,
  spaced_rep_fraction: 1.0,
  debut_phrases_fraction: 1.0,
  skip_voice2: false,
  /**
   * NO PHRASE-COUNT INFLATION (Tom's ruling, 2026-08-07: "JUST DOUBLE").
   *
   * Easy plays the SAME phrase set as Fast — every override here is empty, so
   * the global script_shape row comes through byte for byte — and its extra
   * repetition comes entirely from playing each cycle twice. That is ~2x a
   * Fast round. Easy previously ALSO doubled the phrase COUNTS (14/4/24/6
   * against the global 7/2/12/3), which compounded with the doubling into a
   * ~2.5x round nobody asked for: more DIFFERENT phrases, where what was asked
   * for was the SAME phrase heard twice.
   *
   * This stays here as the knob rather than being deleted: it IS the
   * phrase-count multiplier, editable per mode from the admin Speaking page,
   * and it defaults to no inflation.
   */
  scriptShape: {},
  // HALVE the longest possible phrase (Aran, 2026-08-06) — a fraction of the
  // COURSE's longest phrase, measured in characters. Phrases still arrive
  // shortest-first; the cap only cuts the long tail, with the starvation guard
  // in capPhrasesByLength standing behind it.
  maxPhraseLengthFraction: 0.5,
  /**
   * DOUBLE EVERY PRACTICE CYCLE (Tom, 2026-08-07). Easy's repetition comes
   * from hearing each phrase twice in a row rather than from meeting more
   * different phrases: "we want more repetitions in each ROUND for a LEGO,
   * but we do NOT ever want to repeat exactly the same phrase more than 2x".
   * 2 is also the hard ceiling — see normalizePhraseRepeatCount.
   */
  phraseRepeatCount: 2,
  /** BLD, REVIEW, USE and CONSOLIDATE — the four he named. Intro and the bare
   *  LEGO are absent: "of course not - the intro LEGO and not the LEGO alone". */
  repeatedCycleTypes: ['build', 'spaced_rep', 'use'],
  /**
   * NO filtering on BUILD phrases — Tom, 2026-08-07, verbatim. The debut round
   * hands the learner the new LEGO and then "all the places that it can fit
   * in"; those fragments are short already, so a length filter only thins the
   * one round that exists to be generous.
   */
  filterBuildPhrases: false,
  /**
   * KNOWN-side pull filter for REVIEW and CONSOLIDATE slots: prefer a use
   * phrase of at most 15 syllables in the learner's OWN language, for the
   * first 100 rounds of the course. Replaces the flat 20-target-syllable
   * whole-mode ceiling that shipped earlier on 2026-08-07 — that counted the
   * wrong side of the pair, applied to the whole script, and never came off.
   *
   * Both numbers are DB rows (`algorithm_config.easy_mode`), so retuning by
   * ear is a Supabase edit, not a deploy.
   */
  reviewMaxKnownSyllables: 15,
  reviewSyllableFilterMaxRound: 100,
  /**
   * SLIDING WORD CAP on USE phrases — 8 words to round 20, 10 to round 100,
   * uncapped after (Tom, 2026-08-09). Counted on the ENGLISH side of the pair,
   * and live ONLY on courses with English on one side; the 47 courses with
   * English on neither side are explicitly deferred and take no cap.
   */
  useWordCapTiers: [...DEFAULT_EASY_USE_WORD_CAP_TIERS],
  /**
   * LISTENING — native pace, 1.0×, the same as Fast.
   *
   * SUPERSEDED 2026-08-10 (Tom, testing listening live): "'Easy' seems to have
   * slowed the conversations in the listening section - I don't think we want
   * to be doing that... I think we can return the default listening speed
   * settings to 1.0x on EASY, I think we moved them to 0.8x."
   *
   * That supersedes his 2026-08-07 "it should be slower on easy mode in the
   * listening", and the ramp it produced (0.7 once, 0.8 for four, then 1.0).
   * Easy is still gentler than Fast everywhere else on this row — longer
   * pauses, doubled reps, shorter phrases; it is the LISTENING SPEED alone
   * that goes back to 1.0. Still a DB row
   * (`algorithm_config.easy_mode.listeningSpeedRamp`), so the gentle curve is
   * one Supabase edit away.
   */
  listeningSpeedRamp: DEFAULT_EASY_LISTENING_RAMP,
  /**
   * The BELT CEILING the ramp above lives underneath — now a single no-op rung
   * at 1.0, the same as Fast's.
   *
   * SUPERSEDED 2026-08-10 by the same ruling. Tom's 2026-08-07 23:56Z table
   * (white/yellow 0.8, orange/green 0.9, blue+ 1.0) was a HARD ceiling, so an
   * early-course Easy learner was pinned at 0.8 for ever however often they met
   * a phrase — the specific thing he heard. Still a DB row
   * (`algorithm_config.easy_mode.listeningBeltCeilings`).
   */
  listeningBeltCeilings: DEFAULT_EASY_BELT_CEILINGS,
  // Pods twice as often as Fast — "they need to come more often on EASY MODE"
  // (Tom, 2026-09-18).
  podRoundInterval: 2,
}

/**
 * THE POD CADENCE FOR A MODE, in completed rounds per lap (Tom, 2026-09-18:
 * every 2 rounds on Easy, every 4 on Fast). The mode's own value wins; the
 * global `algorithm_config.pods.roundInterval` is the fallback for a stored
 * mode row that predates the knob; 1 (every round) is the last resort, which
 * is what the scheduler has always defaulted to.
 */
export function resolvePodRoundInterval(
  mode?: Partial<Pick<ModeConfig, 'podRoundInterval'>> | null,
  pods?: Partial<Pick<PodsConfig, 'roundInterval'>> | null,
): number {
  const fromMode = mode?.podRoundInterval
  if (typeof fromMode === 'number' && Number.isFinite(fromMode) && fromMode >= 1) {
    return Math.floor(fromMode)
  }
  const fromPods = pods?.roundInterval
  if (typeof fromPods === 'number' && Number.isFinite(fromPods) && fromPods >= 1) {
    return Math.floor(fromPods)
  }
  return 1
}

/**
 * Layer a mode's optional `scriptShape` over the global `script_shape` row.
 * THE single place this merge happens — modes differ by OVERRIDE, never by a
 * parallel shape object. A mode with no override (Fast) resolves to the
 * global row byte-for-byte.
 */
export function resolveScriptShape(
  global: ScriptShapeConfig,
  mode?: Pick<ModeConfig, 'scriptShape'> | null,
): ScriptShapeConfig {
  return { ...global, ...(mode?.scriptShape || {}) }
}

/**
 * Coerce a mode's `maxPhraseLengthFraction` into (0, 1].
 * Anything missing, non-finite, ≤0 or >1 degrades to 1.0 — UNCAPPED, i.e. the
 * historic behaviour. A bad DB value must never silently shorten a course.
 */
export function normalizeMaxPhraseLengthFraction(fraction?: number | null): number {
  if (typeof fraction !== 'number' || !Number.isFinite(fraction)) return 1
  if (fraction <= 0 || fraction > 1) return 1
  return fraction
}

/** Warned-once ledger for a config row asking for more repeats than the rule allows. */
let repeatCeilingWarned = false

/**
 * Coerce a mode's `phraseRepeatCount` into 1..MAX_PHRASE_REPEAT_COUNT.
 *
 * Absent / non-finite / ≤1 ⇒ 1, i.e. no repetition, which is Fast's value.
 * ABOVE THE CEILING ⇒ clamped to 2, loudly. That ceiling is Tom's rule, not a
 * preference — "a phrase repeated 3x would drive people nuts" — so it is the
 * one thing on this row config cannot raise.
 */
export function normalizePhraseRepeatCount(count?: number | null): number {
  if (typeof count !== 'number' || !Number.isFinite(count)) return 1
  const floored = Math.floor(count)
  if (floored <= 1) return 1
  if (floored > MAX_PHRASE_REPEAT_COUNT) {
    if (!repeatCeilingWarned) {
      repeatCeilingWarned = true
      console.warn(
        `[mode-config] phraseRepeatCount ${floored} exceeds the hard ceiling of ${MAX_PHRASE_REPEAT_COUNT} and has been clamped. ` +
        'Tom, 2026-08-07: "we do NOT ever want to repeat exactly the same phrase more than 2x - a phrase repeated 3x would drive people nuts."',
      )
    }
    return MAX_PHRASE_REPEAT_COUNT
  }
  return floored
}

/**
 * Coerce a mode's `repeatedCycleTypes` into a set of cycle types.
 * Absent / not an array ⇒ the four types Tom named. An EMPTY array is honoured
 * as "repeat nothing" — that is a deliberate configuration, not a bad value.
 */
export function normalizeRepeatedCycleTypes(types?: string[] | null): ReadonlySet<string> {
  if (!Array.isArray(types)) return new Set(DEFAULT_REPEATED_CYCLE_TYPES)
  return new Set(types.filter((t) => typeof t === 'string' && t.length > 0))
}

/**
 * Coerce a mode's `reviewMaxKnownSyllables` into a positive limit, or Infinity.
 * Anything missing, non-finite or ≤0 degrades to Infinity — NO FILTER, i.e.
 * Fast's behaviour. Same degrade-to-permissive discipline as
 * normalizeMaxPhraseLengthFraction above: a bad DB value must never silently
 * narrow what a learner meets. Non-integers are floored, so 15.7 filters at 15
 * rather than admitting a phantom half-syllable.
 */
export function normalizeMaxKnownSyllables(max?: number | null): number {
  if (typeof max !== 'number' || !Number.isFinite(max)) return Infinity
  if (max <= 0) return Infinity
  return Math.floor(max)
}

/**
 * Coerce `reviewSyllableFilterMaxRound` into a positive round number.
 * Absent / non-finite / ≤0 degrades to the shipped 100 — the filter's whole
 * point is that it lifts, so a bad DB value must never leave it on for ever.
 */
export const DEFAULT_REVIEW_FILTER_MAX_ROUND = 100
export function normalizeReviewFilterMaxRound(round?: number | null): number {
  if (typeof round !== 'number' || !Number.isFinite(round)) return DEFAULT_REVIEW_FILTER_MAX_ROUND
  if (round <= 0) return DEFAULT_REVIEW_FILTER_MAX_ROUND
  return Math.floor(round)
}

/** Warned-once ledger for the known-side filter going inert, keyed by course. */
const syllableCapWarnedCourses = new Set<string>()

/** What `makeKnownSyllableResolver` hands back. */
export interface PhraseSyllableResolver {
  /** The registry key derived from the course's `known_lang`. */
  lang: string
  /** False ⇒ no counter for this language ⇒ the filter is INERT here. */
  countable: boolean
  /**
   * A phrase's KNOWN-side syllables, or null when uncountable — in which case
   * the filter cannot judge this phrase and must let it through.
   */
  syllablesOf: (phrase: { known_text?: string | null }) => number | null
}

/**
 * THE one place a phrase's KNOWN-side syllable count is resolved for the
 * review/consolidate pull filter (Tom, 2026-08-07 — the filter counts the
 * learner's own language, not the target).
 *
 * There is no stored known-side count anywhere in the schema, so this is the
 * canonical counter for the course's KNOWN language (@ssi/core/text, ported
 * verbatim from Popty's tools/lib/syllable-counters.cjs) or nothing.
 *
 * WHEN THERE IS NO COUNTER this returns `countable: false` and warns ONCE per
 * course. It does NOT throw, and it does NOT guess with another language's
 * rules. The filter simply does not apply, and says so — which is precisely
 * how the FIRST syllable attempt failed: it computed a ceiling from an all-1s
 * heuristic and silently did nothing. Loud inertness is the fix. English is
 * the known language of most courses and English is registered, so the filter
 * is live on the great majority of the estate; the character cap still stands
 * behind it everywhere.
 */
export function makeKnownSyllableResolver(
  courseCode: string,
  knownLang: string | null | undefined,
): PhraseSyllableResolver {
  const lang = syllableLangOf(knownLang)
  const countable = hasSyllableCounter(lang)
  // Review pools recur across rounds. Retain counts only for this resolver's
  // language/lifetime, keyed by text so edited phrases are counted afresh.
  const counts = new Map<string, number>()

  if (!countable && !syllableCapWarnedCourses.has(courseCode)) {
    syllableCapWarnedCourses.add(courseCode)
    console.warn(
      `[phrase-cap] reviewMaxKnownSyllables is INERT for ${courseCode}: no syllable counter registered for known language '${lang || '(unknown)'}'. ` +
      'Review and consolidate phrases will NOT be filtered by known-side syllable count on this course — the maxPhraseLengthFraction character cap is the only length cap in force. ' +
      'To make it apply, add a counter to packages/core/src/text/syllables.ts and mirror it into ssi-dashboard-v7-clean/tools/lib/syllable-counters.cjs.',
    )
  }

  return {
    lang,
    countable,
    syllablesOf: (phrase) => {
      if (!countable) return null
      const text = phrase.known_text
      if (!text) return null
      const cached = counts.get(text)
      if (cached !== undefined) return cached
      const count = countSyllables(text, lang)
      if (counts.size >= 1024) counts.delete(counts.keys().next().value!)
      counts.set(text, count)
      return count
    },
  }
}

/**
 * Phrase-pool ordering, the length cap and the known-side pull filter now live
 * ONCE, in `@ssi/core`'s `script/phraseSelection.ts` — "selection is pedagogy;
 * bundling is plumbing" (Tom, 2026-08-29). They used to live here AND, in a
 * different form, in the bundle path's generator, which is how the two
 * producers came to disagree about which phrase fills a slot. Re-exported so
 * every existing importer of this module keeps working unchanged; the full
 * rules and their history are documented at the definitions.
 */
export {
  phraseTextLength,
  courseMaxPhraseLength,
  capPhrasesByLength,
  filterReviewPool,
  type ReviewPullFilter,
} from '@ssi/core'

/**
 * Coerce a mode's `useWordCapTiers` into a valid, ascending ladder.
 * Anything missing, not an array, or with no usable rung degrades to `[]` —
 * NO CAP, i.e. Fast's behaviour and today's behaviour. Same degrade-to-
 * permissive discipline as every other normalizer here: a bad DB value must
 * never silently narrow what a learner meets. Non-integers are floored.
 */
export function normalizeUseWordCapTiers(tiers?: readonly UseWordCapTier[] | null): UseWordCapTier[] {
  if (!Array.isArray(tiers)) return []
  return tiers
    .filter((t): t is UseWordCapTier =>
      !!t &&
      typeof t.maxRound === 'number' && Number.isFinite(t.maxRound) && t.maxRound > 0 &&
      typeof t.maxWords === 'number' && Number.isFinite(t.maxWords) && t.maxWords > 0)
    .map((t) => ({ maxRound: Math.floor(t.maxRound), maxWords: Math.floor(t.maxWords) }))
    .sort((a, b) => a.maxRound - b.maxRound)
}

/** True for any spelling of English as a course's known/target language. */
export function isEnglishLang(lang: string | null | undefined): boolean {
  const base = syllableLangOf(lang)
  return base === 'eng' || base === 'en'
}

/**
 * Which side of a course's pair is ENGLISH — the side the word cap counts.
 * `null` ⇒ English is on neither side ⇒ NO CAP (Tom, 2026-08-09: the sliding
 * scale was validated on English-involved pairs only; the rest is deferred).
 * Known wins if both sides are somehow English.
 */
export function englishSideOfCourse(
  knownLang: string | null | undefined,
  targetLang: string | null | undefined,
): 'known' | 'target' | null {
  if (isEnglishLang(knownLang)) return 'known'
  if (isEnglishLang(targetLang)) return 'target'
  return null
}

const englishWordCounts = new Map<string, number>()
const MAX_WORD_COUNT_TEXTS = 1024

/**
 * Words in a stretch of English text. Punctuation and stray whitespace do not
 * count; a contraction ("don't") and a hyphenated compound ("twenty-one") each
 * count as the one word a reader would say.
 */
export function countEnglishWords(text: string | null | undefined): number {
  if (!text) return 0
  // USE pools recur across rounds. Key by text so edits are counted afresh,
  // and bound retention across courses; only the count is reused, never a cap.
  const cached = englishWordCounts.get(text)
  if (cached !== undefined) return cached
  const words = text.match(/[\p{L}\p{N}]+(?:['’’-][\p{L}\p{N}]+)*/gu)
  const count = words ? words.length : 0
  if (englishWordCounts.size >= MAX_WORD_COUNT_TEXTS) {
    englishWordCounts.delete(englishWordCounts.keys().next().value!)
  }
  englishWordCounts.set(text, count)
  return count
}

/** The resolved USE word cap for one run: which side to count, and the ladder. */
export interface UseWordCap {
  side: 'known' | 'target'
  tiers: readonly UseWordCapTier[]
}

/**
 * Resolve the word cap for a run, or `null` when it does not apply.
 * Null on: no tiers (Fast, or a malformed row), or English on neither side of
 * the course. Both are "behave exactly as today", never "guess".
 */
export function makeUseWordCap(
  knownLang: string | null | undefined,
  targetLang: string | null | undefined,
  tiers?: readonly UseWordCapTier[] | null,
): UseWordCap | null {
  const ladder = normalizeUseWordCapTiers(tiers)
  if (ladder.length === 0) return null
  const side = englishSideOfCourse(knownLang, targetLang)
  if (!side) return null
  return { side, tiers: ladder }
}

/** The English-side word ceiling on a given round; Infinity past the ladder. */
export function useWordLimitForRound(cap: UseWordCap | null | undefined, roundNumber: number): number {
  if (!cap) return Infinity
  for (const tier of cap.tiers) {
    if (roundNumber <= tier.maxRound) return tier.maxWords
  }
  return Infinity
}

/**
 * THE one place the Easy USE word cap is applied (Tom, 2026-08-09).
 *
 * Given a pool of USE phrases and the round being generated, return the
 * sub-pool the round may draw from:
 *   1. no cap, or past the last rung ⇒ the pool untouched;
 *   2. otherwise keep phrases of at most the rung's words on the ENGLISH side.
 *      A phrase with no text on that side cannot be judged and passes;
 *   3. STARVATION GUARD — if that leaves NOTHING, the cap comes off for this
 *      round and the whole pool comes back. A capped round is never an empty
 *      round; phrase volume is the harder rail.
 */
export function capUsePoolByWords<T extends { known_text?: string | null; target_text?: string | null }>(
  pool: readonly T[],
  roundNumber: number,
  cap: UseWordCap | null | undefined,
): readonly T[] {
  if (!cap || pool.length === 0) return pool
  const limit = useWordLimitForRound(cap, roundNumber)
  if (!Number.isFinite(limit)) return pool

  const kept = pool.filter((phrase) => {
    const words = countEnglishWords(cap.side === 'known' ? phrase.known_text : phrase.target_text)
    if (words === 0) return true // nothing to judge ⇒ passes
    return words <= limit
  })
  return kept.length > 0 ? kept : pool
}

/** Methodology per-LEGO phrase floors the cap must never breach (ralph: >=4
 *  BUILD, >=5 USE — "fewer phrases is a FAIL"). */
export const MIN_BUILD_PHRASES_AFTER_CAP = 4
export const MIN_USE_PHRASES_AFTER_CAP = 5

const DEFAULT_LISTENING: ListeningModeConfig = {
  enabled: true,
  offset: 90,
  // The 2026-08-07 simplification: ONE pattern, exposure-ramped, capped at 1.0,
  // and the nine-stage playlist off. See listeningExposureRamp.ts.
  playPattern: DEFAULT_LISTENING_PATTERN,
  maxSpeed: LISTENING_SPEED_CEILING,
  speedSource: DEFAULT_LISTENING_SPEED_SOURCE,
  listeningUseStagePlaylist: false,
}

/**
 * Resolve the whole listening play/speed policy from the live `listening` row
 * plus the active mode's `listeningSpeedRamp`. THE one place the DB row is
 * turned into something the two listening schedulers can use, so Layer 1 and
 * Layer 2 provably agree.
 *
 * Degrade-to-gentle, not degrade-to-permissive: every malformed value falls
 * back to the shipped default rather than to "off". A missing ramp must never
 * mean full speed for a beginner.
 */
export interface ListeningPlayPolicy {
  pattern: ListeningSlot[]
  ramp: ListeningRampStep[]
  /** Per-mode belt ceilings — the maximum the ramp may approach, by learner
   *  position. See ModeConfig.listeningBeltCeilings. */
  beltCeilings: ListeningBeltCeiling[]
  ceiling: number
  speedSource: ListeningSpeedSource
  /** True ⇒ replay the retired nine-stage playlist (escape hatch only). */
  useStagePlaylist: boolean
}

export function resolveListeningPlayPolicy(
  listening: Partial<ListeningModeConfig> | null | undefined,
  mode: LearningMode,
  modeConfig?: Partial<ModeConfig> | null,
): ListeningPlayPolicy {
  const shippedRamp = mode === 'easy' ? DEFAULT_EASY_LISTENING_RAMP : DEFAULT_FAST_LISTENING_RAMP
  const shippedBelts = mode === 'easy' ? DEFAULT_EASY_BELT_CEILINGS : DEFAULT_FAST_BELT_CEILINGS
  const rawCeiling = listening?.maxSpeed
  const ceiling = typeof rawCeiling === 'number' && Number.isFinite(rawCeiling) && rawCeiling > 0
    ? Math.min(rawCeiling, LISTENING_SPEED_CEILING)
    : LISTENING_SPEED_CEILING
  const source = listening?.speedSource === 'belt' ? 'belt' : DEFAULT_LISTENING_SPEED_SOURCE
  return {
    pattern: resolveListeningPattern(listening?.playPattern),
    ramp: normalizeListeningRamp(modeConfig?.listeningSpeedRamp, shippedRamp),
    beltCeilings: normalizeBeltCeilings(modeConfig?.listeningBeltCeilings, shippedBelts),
    ceiling,
    speedSource: source,
    useStagePlaylist: listening?.listeningUseStagePlaylist === true,
  }
}

export const DEFAULT_PODS: PodsConfig = {
  // THE NINE-RUNG LADDER, rung by rung as Tom ruled it (2026-09-20, closed
  // with "This is good"). Stages 1 to 8 are visited ONCE each, stage 9 twice,
  // then the sentence retires — ten visits in total (that count and the
  // retirement arithmetic are job #318's and are unchanged here).
  //
  // THE PRINCIPLE, in the words Tom accepted: each rung removes exactly ONE
  // thing — meaning at 5, slow play at 7, then repeats — so SPEED ONLY RISES
  // and LENGTH ONLY FALLS, all the way down. The known line carries meaning
  // for four rungs; the slow target play survives six.
  //
  // This map is MIRRORED, character for character, to the live `pods` row in
  // algorithm_config, which is what learners actually hear (the row overrides
  // these defaults wherever it loads). Change one, change the other in the
  // same edit — they have drifted before.
  //
  // ps = pod sentence at 1.0x, ps2x = at 2.0x, trans = known-language audio.
  stagePlaylist: {
    '1': ['ps', 'trans', 'ps', 'ps'],
    '2': ['ps', 'trans', 'ps', 'ps'],
    '3': ['ps', 'trans', 'ps', 'ps2x'],
    '4': ['ps', 'trans', 'ps2x', 'ps2x'],
    '5': ['ps', 'ps2x', 'ps2x'],
    '6': ['ps', 'ps2x'],
    '7': ['ps2x', 'ps2x'],
    '8': ['ps2x'],
    '9': ['ps2x'],
  },
  stageDuration: 5,
  // Every stage 1..8 LISTED at one lap each — an unlisted stage falls back to
  // stageDuration (5), the opposite of the ruling. Stage 9 is deliberately
  // absent: it is the terminal rung and its dwell is whatever retireAfterVisits
  // leaves, which with these eight is exactly 2.
  stageDurations: { '1': 1, '2': 1, '3': 1, '4': 1, '5': 1, '6': 1, '7': 1, '8': 1 },
  retireAfterVisits: 10,
  gapSuperTightMs: 100,
  gapTightMs: 200,
  gapGluedMs: 300,
  gapBetweenMs: 1000,
  /* L2 pod-lap fires every 5 main rounds from activation. Hotfix
   * 2026-05-20: stale activation values (21+ from an earlier rule) are
   * capped at 5 on read in usePodLapScheduler so the first pod surfaces
   * ~5 rounds in (gives the learner time to settle into speaking practice
   * before pods interleave). The growing-interval scheduler (2/2/3/3/4/4
   * /5/5/5/5...) lands separately. */
  roundInterval: 5,
  podActivationRound: 5,
}

const DEFAULT_SCRIPT_SHAPE: ScriptShapeConfig = {
  spacedRepOffsets: [1, 2, 3, 5, 8, 13, 21, 34, 55, 89],
  maxBuildPhrases: 7,
  useConsolidationCount: 2,
  maxSpacedRepPhrases: 12,
  n1PhraseCount: 3,
}

const DEFAULT_RESUME: ResumeConfig = {
  cycleResetMinutes: 5,
  beltRegressionDays: 60,
}

/**
 * Adaptation v2 safety rails (`archive/docs-retired-2026-08-24/adaptation/adaptation-v2-build-spec.md`
 * §6, WP-5). Two independent gates:
 *   - `enabled`: the kill switch. false ⇒ the whole v2 pipeline (evidence →
 *     curvature → RatePolicyEngine → shadow log) never even computes for a
 *     round boundary — not just "computes but doesn't apply". The v1 pause
 *     ladder keeps running untouched regardless.
 *   - `shadow`: when `enabled` is true, gates APPLICATION only. true ⇒ the
 *     engine computes a RoundPlan and logs it (`adaptation_plan` event) every
 *     round boundary, but never touches playback. false ⇒ the plan is
 *     actually applied via SimplePlayer's overrides surface.
 * Shipping default is `enabled:true, shadow:true` — "compute and log, never
 * apply" — which is exactly what "ship with the DB flag absent" needs to mean
 * (executive summary point 9). Flipping `enabled:false` later is the harder
 * kill switch for if the shadow logs themselves look wrong.
 */
export interface AdaptationV2Config {
  enabled: boolean
  shadow: boolean
  /** Stage 2 (envelope metadata, WP-6..9) — separately gated, off until that track ships. */
  stage2_enabled: boolean
  bounds: RatePolicyBounds
  /** Stage-2 delta-producer weights (§5.3) — unused until WP-8 lands; carried here so the shape is stable. */
  weights: { duration: number; peaks: number; shape: number }
}

const DEFAULT_ADAPTATION_V2: AdaptationV2Config = {
  enabled: true,
  shadow: true,
  stage2_enabled: false,
  bounds: DEFAULT_RATE_POLICY_BOUNDS,
  weights: { duration: 0.5, peaks: 0.3, shape: 0.2 },
}

const DEFAULT_META_COMMENTARY: MetaCommentaryConfig = {
  encouragementTaper: DEFAULT_ENCOURAGEMENT_TAPER,
}

/**
 * Resolve the live `pods` row against the shipped defaults. Field-level merge,
 * so a partial row never drops a field.
 *
 * SPREAD, NEVER `||`. Every gap in the live row is 0 (Tom, 2026-08-24: the
 * fade launches on a hard cut, no pause between clips or between sentences),
 * and 0 is falsy — a `row.gapBetweenMs || DEFAULT_PODS.gapBetweenMs` anywhere
 * on this path would silently restore the 1000 ms between-sentence pause and
 * nobody would see an error. The test locks that.
 *
 * Exported for tests.
 */
export function resolvePodsConfig(loaded: Partial<PodsConfig> | null | undefined): PodsConfig {
  return { ...DEFAULT_PODS, ...(loaded || {}) }
}

/** Read the taper out of a loaded DB row, honouring only the known
 *  minute-denominated keys — see the call site for why. Exported for tests. */
export function pickTaper(loaded: any): EncouragementTaperConfig {
  const num = (v: unknown, fallback: number) =>
    typeof v === 'number' && Number.isFinite(v) ? v : fallback
  return {
    taperStartMinutes: num(loaded?.taperStartMinutes, DEFAULT_ENCOURAGEMENT_TAPER.taperStartMinutes),
    offAtMinutes: num(loaded?.offAtMinutes, DEFAULT_ENCOURAGEMENT_TAPER.offAtMinutes),
  }
}

// Singleton cache - shared across all component instances
let configCache: AlgorithmConfigs | null = null
let cacheTimestamp: number = 0
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

// Share only in-flight reads from the same client. Each caller still applies
// the result to its own refs, and failures are removed so later loads retry.
const pendingConfigReads = new WeakMap<object, Promise<any>>()
function readConfigRows(client: any): Promise<any> {
  const pending = pendingConfigReads.get(client)
  if (pending) return pending
  const request = Promise.resolve(client.from('algorithm_config').select('key, config'))
    .finally(() => { pendingConfigReads.delete(client) })
  pendingConfigReads.set(client, request)
  return request
}

export function useAlgorithmConfig(supabase: Ref<any> | null) {
  const configs = ref<AlgorithmConfigs>({
    fast_mode: DEFAULT_FAST,
    easy_mode: DEFAULT_EASY,
    listening: DEFAULT_LISTENING,
    pods: DEFAULT_PODS,
    script_shape: DEFAULT_SCRIPT_SHAPE,
    resume: DEFAULT_RESUME,
    adaptation_v2: DEFAULT_ADAPTATION_V2,
    meta_commentary: DEFAULT_META_COMMENTARY,
  })
  const isLoaded = ref(false)
  const loadError = ref<string | null>(null)

  // Load configs from Supabase (with caching)
  const loadConfigs = async (forceRefresh = false): Promise<void> => {
    // Check cache first
    const now = Date.now()
    if (!forceRefresh && configCache && (now - cacheTimestamp) < CACHE_TTL_MS) {
      configs.value = configCache
      isLoaded.value = true
      return
    }

    if (!supabase?.value) {
      console.warn('[AlgorithmConfig] Supabase not available, using defaults')
      isLoaded.value = true
      return
    }

    try {
      const { data, error } = await readConfigRows(supabase.value)

      if (error) {
        console.error('[AlgorithmConfig] Fetch error:', error)
        loadError.value = error.message
        // Fall back to defaults
        isLoaded.value = true
        return
      }

      if (data && data.length > 0) {
        const loaded: Record<string, any> = {}
        for (const row of data) {
          loaded[row.key] = row.config
        }

        // Merge with defaults (in case some keys are missing). Field-level
        // merge for keyed configs so a partial DB row doesn't drop fields.
        configs.value = {
          ...loaded,
          // Promotion-window read: 'fast_mode' is the new key, 'normal_mode'
          // is the live fallback alias it was copied from. An old bundle
          // reading a new DB and a new bundle reading an old DB both work —
          // whichever row exists wins, new key first. NOTE 'normal_mode' is
          // NOT retired-mode residue — it is fast_mode's live alias and must
          // stay until the promotion window closes.
          fast_mode: { ...DEFAULT_FAST, ...(loaded.fast_mode || loaded.normal_mode || {}) },
          easy_mode: { ...DEFAULT_EASY, ...(loaded.easy_mode || {}) },
          listening: { ...DEFAULT_LISTENING, ...(loaded.listening || {}) },
          pods: resolvePodsConfig(loaded.pods),
          script_shape: { ...DEFAULT_SCRIPT_SHAPE, ...(loaded.script_shape || {}) },
          resume: { ...DEFAULT_RESUME, ...(loaded.resume || {}) },
          adaptation_v2: {
            ...DEFAULT_ADAPTATION_V2,
            ...(loaded.adaptation_v2 || {}),
            bounds: { ...DEFAULT_ADAPTATION_V2.bounds, ...(loaded.adaptation_v2?.bounds || {}) },
            weights: { ...DEFAULT_ADAPTATION_V2.weights, ...(loaded.adaptation_v2?.weights || {}) },
          },
          meta_commentary: {
            ...DEFAULT_META_COMMENTARY,
            ...(loaded.meta_commentary || {}),
            // Pick ONLY the known (minute-denominated) keys. A stale row still
            // carrying the pre-2026-08-06 seed keys (taperStartSeeds /
            // offAtSeeds) must not leak through — it would silently switch
            // encouragements off after 8 MINUTES. Unknown keys are ignored and
            // the new defaults stand.
            encouragementTaper: pickTaper(loaded.meta_commentary?.encouragementTaper),
          },
        }

        // Update cache
        configCache = configs.value
        cacheTimestamp = now

        console.log('[AlgorithmConfig] Loaded from Supabase:', Object.keys(loaded))
      }

      isLoaded.value = true
    } catch (err) {
      console.error('[AlgorithmConfig] Unexpected error:', err)
      loadError.value = String(err)
      isLoaded.value = true
    }
  }

  // Convenience getters
  const fastConfig = computed(() => configs.value.fast_mode as ModeConfig)
  const easyConfig = computed(() => configs.value.easy_mode as ModeConfig)
  /** The ModeConfig for a given learning mode. */
  const modeConfig = (mode: LearningMode): ModeConfig =>
    mode === 'easy' ? easyConfig.value : fastConfig.value
  const listeningConfig = computed(() => configs.value.listening as ListeningModeConfig)
  const podsConfig = computed(() => configs.value.pods as PodsConfig)
  const scriptShapeConfig = computed(() => configs.value.script_shape as ScriptShapeConfig)
  /** The effective script shape for a mode: the global `script_shape` row with
   *  that mode's `scriptShape` override layered on top. Fast carries no
   *  override, so it resolves to the global row unchanged. */
  const scriptShapeForMode = (mode: LearningMode): ScriptShapeConfig =>
    resolveScriptShape(scriptShapeConfig.value, modeConfig(mode))
  /** Pod-lap cadence for a mode: 2 rounds on Easy, 4 on Fast (Tom, 2026-09-18). */
  const podRoundIntervalForMode = (mode: LearningMode): number =>
    resolvePodRoundInterval(modeConfig(mode), podsConfig.value)
  const resumeConfig = computed(() => configs.value.resume as ResumeConfig)
  const adaptationV2Config = computed(() => configs.value.adaptation_v2 as AdaptationV2Config)
  const metaCommentaryConfig = computed(() => configs.value.meta_commentary as MetaCommentaryConfig)

  // Get any config by key
  const getConfig = (key: string): ModeConfig | ListeningModeConfig | PodsConfig | ScriptShapeConfig | ResumeConfig | AdaptationV2Config | MetaCommentaryConfig | null => {
    return configs.value[key] || null
  }

  // Calculate pause duration based on config and target audio length.
  // Delegates to the one source of truth (@ssi/core) — `targetDurationMs` is
  // the native 1.0× duration of the target sentence.
  const calculatePause = (config: ModeConfig, targetDurationMs: number): number =>
    computePauseDuration(targetDurationMs, targetDurationMs, config)

  // Invalidate cache (call after admin updates)
  const invalidateCache = () => {
    configCache = null
    cacheTimestamp = 0
  }

  return {
    configs,
    isLoaded,
    loadError,
    loadConfigs,
    fastConfig,
    easyConfig,
    modeConfig,
    scriptShapeForMode,
    podRoundIntervalForMode,
    listeningConfig,
    podsConfig,
    scriptShapeConfig,
    resumeConfig,
    adaptationV2Config,
    metaCommentaryConfig,
    getConfig,
    calculatePause,
    invalidateCache,
    // Export defaults for reference
    DEFAULT_FAST,
    DEFAULT_EASY,
    DEFAULT_LISTENING,
    DEFAULT_PODS,
    DEFAULT_SCRIPT_SHAPE,
    DEFAULT_RESUME,
    DEFAULT_ADAPTATION_V2,
    DEFAULT_META_COMMENTARY,
  }
}
